import { StepName, StepProps } from "../../types";
import { useSignupStore } from "components/Signup/store/hooks";
import {
  getCurrentDraftForAnimal,
  useAllValidAnimals,
  useCurrentAnimal,
} from "components/Signup/store/selectors/animals";
import { useCurrentInsuranceByAnimalId } from "components/Signup/store/selectors/insurance";
import { updateInsuranceById } from "components/Signup/store/slices/animals";
import Overlay from "components/Signup/components/Overlay";
import { ReactNode, useState } from "react";
import { QuotePreviewBottomBar } from "components/Signup/components/QuotePreviewBottomBar";
import StickyBottom from "components/Signup/components/StickyBottom/StickyBottom";
import { BuyButton } from "components/Signup/components/BuyButton";
import { formatCurrency } from "utils/utils";
import Gutter from "ui-kit/Gutter";
import { useIsLoading } from "components/Signup/store/selectors/metadata";
import { getAmountToPay } from "utils/insurance/getAmountToPay";
import { Typography } from "ui-kit/Typography";
import { useWebTranslation } from "translations";
import ChooseLifeInsurance from "components/Signup/components/ChooseLifeInsurance/ChooseLifeInsurance";
import {
  MAX_LIFE_LIMIT_CAT,
  MAX_LIFE_LIMIT_DOG,
  MIN_LIFE_LIMIT_CAT,
  MIN_LIFE_LIMIT_DOG,
} from "components-shared/insuranceData";
import { SpeciesEnum } from "../../../../../../__generated__/globalTypes";
import { AddonBox } from "components/Signup/components/AddonBox";
import { UpsertDraft_upsertDraft_policyPreviews_plans } from "../../../../../../__generated__/UpsertDraft";
import { YEARLY_BILLING_INTERVAL } from "utils/insurance";
import shieldDog from "../../../../../../public/images/illustrations/shield-dog.svg";
import shieldCat from "../../../../../../public/images/illustrations/shield-cat.svg";
import familyDog from "../../../../../../public/images/illustrations/family-dog.svg";
import familyCat from "../../../../../../public/images/illustrations/family-cat.svg";
import { trackSignupEvent } from "components/Signup/analytics";

const isNumber = (x: string | number): x is number => {
  return Number.isFinite(x);
};

const getPreExistingConditionsPrice = ({
  limit,
  deductible,
  billingInterval,
  copay,
  drafts,
}: {
  limit: number;
  deductible: number;
  billingInterval: number;
  copay: number;
  drafts: UpsertDraft_upsertDraft_policyPreviews_plans[] | null | undefined;
}) => {
  if (!drafts) {
    return "";
  }

  const filteredDrafts = drafts.filter(
    (draft) =>
      draft.limit === limit &&
      draft.deductible === deductible &&
      draft.copay === copay
  );

  const { total: priceWithPreexistingConditions } =
    filteredDrafts.find((draft) => draft.ratingsData?.preexistingConditions) ||
    {};
  const { total: priceWithoutPreexistingConditions } =
    filteredDrafts.find((draft) => !draft.ratingsData?.preexistingConditions) ||
    {};

  if (!priceWithPreexistingConditions || !priceWithoutPreexistingConditions) {
    return "";
  }

  return (
    ((priceWithPreexistingConditions - priceWithoutPreexistingConditions) /
      100 /
      12) *
    billingInterval
  );
};

const Addons = ({ next, isValid, transitioning }: StepProps) => {
  const { t } = useWebTranslation();

  const animals = useAllValidAnimals();

  const animal = useCurrentAnimal();

  const {
    billingInterval,
    id,
    startDate,
    lifeLimit,
    lifeUtilityAddOn,
    showLifeInsurance,
    breedersAddOn,
    preexistingConditions,
    limit,
    copay,
    deductible,
    drafts,
  } = useCurrentInsuranceByAnimalId(animal.id);

  const preExistingConditionsPrice = getPreExistingConditionsPrice({
    limit,
    deductible,
    billingInterval,
    copay,
    drafts,
  });

  const { dispatch, getState } = useSignupStore();
  const isLoading = useIsLoading();

  const currentDraft = getCurrentDraftForAnimal(animal);
  const amountToPay = getAmountToPay({
    ...currentDraft,
    billingInterval,
  });

  const [overlayDisplay, setOverlayDisplay] = useState<{
    title: string;
    content: ReactNode;
    full?: boolean;
  } | null>(null);

  return (
    <>
      <div
        style={{
          maxWidth: 600,
          margin: "0 auto",
          padding: 16,
          display: "flex",
          flexDirection: "column",
          flex: 1,
        }}
      >
        <Typography variant="h2" align="center">
          {t("signup.step.addons.title", {
            name: animal.name ?? "",
          })}
        </Typography>
        <Gutter size={2} />
        <Gutter size={3} />
        <ChooseLifeInsurance
          key={animal.id}
          startDate={startDate}
          birthDay={animal.birthday}
          lifeAgeGroup={animal.breed?.lifeAgeGroup ?? -1}
          animalName={animal.name ?? ""}
          species={animal.species!}
          lifeLimit={lifeLimit ?? 0}
          lifeUtilityAddOn={lifeUtilityAddOn ?? ""}
          onChangeLifeLimit={(newLifeLimit) => {
            const minLifeLimit =
              animal.species === SpeciesEnum.dog
                ? MIN_LIFE_LIMIT_DOG
                : MIN_LIFE_LIMIT_CAT;
            const maxLifeLimit =
              animal.species === SpeciesEnum.dog
                ? MAX_LIFE_LIMIT_DOG
                : MAX_LIFE_LIMIT_CAT;

            const isValidAmount =
              newLifeLimit >= minLifeLimit && newLifeLimit <= maxLifeLimit;

            if (!isValidAmount) {
              dispatch(
                updateInsuranceById({
                  id,
                  update: { lifeLimit: 0, lifeUtilityAddOn: "" },
                })
              );
            } else {
              trackSignupEvent(
                "LIFE_INSURANCE_SELECTED" as StepName,
                0,
                getState()
              );

              dispatch(
                updateInsuranceById({
                  id,
                  update: { lifeLimit: newLifeLimit },
                })
              );
            }
          }}
          onChangeLifeUtilityAddOn={(lifeUtilityAddOn) =>
            dispatch(
              updateInsuranceById({
                id,
                update: { lifeUtilityAddOn },
              })
            )
          }
          showLifeInsurance={showLifeInsurance!}
          setShowLifeInsurance={(shouldShow) =>
            dispatch(
              updateInsuranceById({
                id,
                update: { showLifeInsurance: shouldShow },
              })
            )
          }
        />
        <Gutter size={2} />
        <AddonBox
          title={t("signup.step.addons.preexisting_conditions.title")}
          summary={t("signup.step.addons.preexisting_conditions.summary")}
          switchDescription={
            isNumber(preExistingConditionsPrice)
              ? `${formatCurrency(preExistingConditionsPrice)}/${
                  billingInterval === YEARLY_BILLING_INTERVAL
                    ? t("global.date.year")
                    : t("global.date.month")
                }`
              : ""
          }
          onChange={() => {
            if (preexistingConditions) {
              trackSignupEvent(
                "PREEXISTING_CONDITIONS_SELECTED" as StepName,
                0,
                getState()
              );
            }

            dispatch(
              updateInsuranceById({
                id,
                update: { preexistingConditions: !preexistingConditions },
              })
            );
          }}
          active={preexistingConditions ?? false}
          image={animal.species === SpeciesEnum.cat ? shieldCat : shieldDog}
          readmore={t("signup.step.addons.preexisting_conditions.description")}
        />

        {animal.sexSituation === "not-castrated" && (
          <>
            <Gutter size={2} />
            <AddonBox
              title={t("choose_breeders_add_on.option_title")}
              summary={t(
                animal.species === SpeciesEnum.cat
                  ? "signup.step.addons.breeders_addon.summary_cat"
                  : "signup.step.addons.breeders_addon.summary_dog"
              )}
              switchDescription={t(
                animal.species === SpeciesEnum.cat
                  ? "choose_breeders_add_on.option_description_cat"
                  : "choose_breeders_add_on.option_description_dog"
              )}
              readmore={t(
                animal.species === SpeciesEnum.cat
                  ? "signup.step.addons.breeders_addon.description_cat"
                  : "signup.step.addons.breeders_addon.description_dog"
              )}
              onChange={() => {
                dispatch(
                  updateInsuranceById({
                    id,
                    update: { breedersAddOn: !breedersAddOn },
                  })
                );
              }}
              active={breedersAddOn ?? false}
              image={animal.species === SpeciesEnum.cat ? familyCat : familyDog}
            />
          </>
        )}

        <Overlay
          display={overlayDisplay}
          onRemove={() => setOverlayDisplay(null)}
        />
      </div>

      {animals.length > 1 ? (
        <>
          <QuotePreviewBottomBar next={next} />
        </>
      ) : (
        <>
          <StickyBottom
            button={
              <BuyButton
                oldAmount={
                  amountToPay.old ? formatCurrency(amountToPay.old) : null
                }
                newAmount={formatCurrency(amountToPay.new)}
                onPress={next}
                loading={isLoading || transitioning}
                disabled={!isValid}
              />
            }
            transparent
          />
          <Gutter size={4} />
        </>
      )}
    </>
  );
};

export default Addons;
